<template>
    <div class="select-appointment-slot">
        <div class="selected-preview">
            <span v-if="localDate">
                {{ readableVersion }}
            </span>
            <span v-else class="no-datetime-selected">
                Select date & time
            </span>
        </div>

        <FlatPickr
            :key="flatpickrKey"
            ref="flatpickr"
            v-model="localDate"
            :config="config"
            class="input-display"
            @on-change="updateDate"
        />

        <transition name="fade">
            <TimeSelectWrap
                v-if="localDate"
                id="time"
                key="time-select"
                v-model="localTime"
                label="Time"
                :options="timeSlots"
                :validation="validation"
                @input="updateTime"
            />
        </transition>

        <ErrorMessages
            v-if="validation.$error"
            label="Appointment Slot"
            :validation="validation"
            class="error-message-wrap"
        />
    </div>
</template>

<script>
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import format from 'date-fns/format';
import isFuture from 'date-fns/isFuture';
import isBefore from 'date-fns/isBefore';
import addMinutes from 'date-fns/addMinutes';
import addDays from 'date-fns/addDays';
import isToday from 'date-fns/isToday';
import maxDate from 'date-fns/max';
import parse from 'date-fns/parse';
import parseISO from 'date-fns/parseISO';
import { Actions } from '@/services/enums';
import TimeSelectWrap from '@/components/form/time-select-wrap.vue';
const ErrorMessages = () => import('@/components/form/error-messages');

const ISO_DATE_FORMAT = 'yyyy-MM-dd';
const TWENTY_FOUR_HOUR_FORMAT = 'HH:mm';
const TWELVE_HOUR_FORMAT = 'h:mm a';
const BASIC_TWELVE_HOUR_FORMAT = 'ha';

export default {
    name: 'SelectAppointmentSlot',

    components: {
        FlatPickr,
        TimeSelectWrap,
        ErrorMessages,
    },

    props: {
        datetime: {
            required: true,
            validator: (prop) =>
                typeof prop === 'string' ||
                prop instanceof Date ||
                prop === null,
        },
        action: {
            type: String,
            required: true,
        },
        dealer: {
            type: Object,
            required: false,
            default: null,
        },
        validation: {
            type: Object,
            required: false,
            default: undefined,
        },
    },

    data() {
        const isInTheFuture = (date) => {
            return isToday(date) || isFuture(date);
        };

        const isWithinRangeBasedOnAction = (date) => {
            const limit = addDays(new Date(), this.daysOut);
            return isBefore(date, limit);
        };

        const dealerIsOpen = (date) => {
            return this.getConstraintsForDay(date).isOpen;
        };

        return {
            // always strings
            localDate: null, // yyyy-MM-dd
            localTime: null, // HH:mm

            // these are updated when component initializes
            minTime: '08:00', // HH:mm
            maxTime: '20:00', // HH:mm

            // set when initialized
            dealerConstraints: {
                weekdays: {
                    isOpen: true,
                    start: null,
                    end: null,
                },
                saturday: {
                    isOpen: true,
                    start: null,
                    end: null,
                },
                sunday: {
                    isOpen: true,
                    start: null,
                    end: null,
                },
            },

            config: {
                inline: true,
                enable: [
                    function (date) {
                        return (
                            isInTheFuture(date) &&
                            isWithinRangeBasedOnAction(date) &&
                            dealerIsOpen(date)
                        );
                    },
                ],
            },

            flatpickrKey: 0, // used to force rerender
        };
    },

    computed: {
        localDatetime() {
            if (this.localDate === null) return null;

            if (this.localTime === null) return parseISO(this.localDate);

            return parseISO(`${this.localDate}T${this.localTime}:00`);
        },

        // "Wednesday, Aug 12th at 3:00PM"
        readableVersion() {
            if (this.localTime === null) {
                return format(parseISO(this.localDate), 'iiii, MMM do');
            }

            return format(this.localDatetime, "iiii, MMM do 'at' h:mm a");
        },

        timeSlots() {
            const start = parseISO(`2020-01-01T${this.minTime}:00`);
            const end = parseISO(`2020-01-01T${this.maxTime}:00`);

            const steps = {};

            // from start to end, get every half-hour increment, NOT including the end time
            for (let step = start; step < end; step = addMinutes(step, 30)) {
                const _24hr = format(step, TWENTY_FOUR_HOUR_FORMAT);
                const _12hr = format(step, TWELVE_HOUR_FORMAT);
                steps[_24hr] = _12hr;
            }

            return steps;
        },

        daysOut() {
            switch (this.action) {
                case Actions.Call:
                    return 3;

                /* appointments */
                case Actions.RemoteAppointment:
                case Actions.DealershipAppointment:
                    return 14;

                default:
                    return 5;
            }
        },
    },

    watch: {
        action() {
            this.triggerFlatpickrRerender();
        },
    },

    created() {
        this.determineDealerConstraints();

        setInterval(() => {
            this.updateTimeConstraints();
        }, 60000);
    },

    methods: {
        // from flatpickr date tool
        updateDate([date]) {
            // we have to set the date ourselves here in order to use it in the side effects below
            this.localDate = format(date, ISO_DATE_FORMAT);
            this.localTime = null;
            this.updateTimeConstraints();
            this.updateDatetime();
        },

        updateTime(time) {
            this.updateDatetime();
        },

        updateDatetime() {
            if (this.localDate !== null && this.localTime !== null) {
                this.$emit('update:datetime', this.localDatetime);
            } else {
                this.$emit('update:datetime', null);
            }
        },

        async updateTimeConstraints() {
            if (this.localDate) {
                this.minTime = this.calculateMinTime();
                this.maxTime = this.calculateMaxTime();
                await this.$nextTick();
            }
        },

        // if today is selected day, finds latest between next hour and start contstraint
        // otherwise returns start contstraint
        calculateMinTime() {
            const startBoundary = this.getConstraintsForSelectedDay().start;

            if (isToday(parseISO(this.localDate))) {
                const nextHour = new Date();
                nextHour.setHours(nextHour.getHours() + 1);
                nextHour.setMinutes(0);

                const max = maxDate([nextHour, startBoundary]);

                return format(max, TWENTY_FOUR_HOUR_FORMAT);
            }

            return format(startBoundary, TWENTY_FOUR_HOUR_FORMAT);
        },

        calculateMaxTime() {
            const endBoundary = this.getConstraintsForSelectedDay().end;
            return format(endBoundary, TWENTY_FOUR_HOUR_FORMAT);
        },

        getConstraintsForDay(date) {
            const day = format(date, 'EEEE');

            if (day === 'Saturday') {
                return this.dealerConstraints.saturday;
            } else if (day === 'Sunday') {
                return this.dealerConstraints.sunday;
            } else {
                return this.dealerConstraints.weekdays;
            }
        },

        // finds dealerConstraints property based on selected day
        getConstraintsForSelectedDay() {
            return this.getConstraintsForDay(parseISO(this.localDate));
        },

        triggerFlatpickrRerender() {
            this.flatpickrKey += 1;
        },

        determineDealerConstraints() {
            const CLOSED = 'Closed';

            const { salesMF, salesSAT, salesSUN } = this.dealer;

            if (salesMF) {
                if (salesMF === CLOSED) {
                    this.dealerConstraints.weekdays.isOpen = false;
                } else {
                    this.dealerConstraints.weekdays.isOpen = true;

                    const times = this.parseStartAndEndTimes(salesMF);
                    this.dealerConstraints.weekdays.start = times.start;
                    this.dealerConstraints.weekdays.end = times.end;
                }
            }

            if (salesSAT) {
                if (salesSAT === CLOSED) {
                    this.dealerConstraints.saturday.isOpen = false;
                } else {
                    this.dealerConstraints.saturday.isOpen = true;

                    const times = this.parseStartAndEndTimes(salesSAT);
                    this.dealerConstraints.saturday.start = times.start;
                    this.dealerConstraints.saturday.end = times.end;
                }
            }

            if (salesSUN) {
                if (salesSUN === CLOSED) {
                    this.dealerConstraints.sunday.isOpen = false;
                } else {
                    this.dealerConstraints.sunday.isOpen = true;

                    const times = this.parseStartAndEndTimes(salesSUN);
                    this.dealerConstraints.sunday.start = times.start;
                    this.dealerConstraints.sunday.end = times.end;
                }
            }
        },

        // comes in as "10am - 6pm"
        // returned as { start: "10:00", end: "18:00" }
        parseStartAndEndTimes(hours) {
            const [startString, endString] = hours.split(' - ');

            const start = parse(
                startString,
                BASIC_TWELVE_HOUR_FORMAT,
                new Date()
            );
            const end = parse(endString, BASIC_TWELVE_HOUR_FORMAT, new Date());

            return { start, end };
        },
    },
};
</script>

<style lang="scss" scoped>
.select-appointment-slot {
    width: 100%;
    max-width: 24rem;
    padding: 2rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    .selected-preview {
        width: 100%;
        max-width: 307.88px; // match calendar below
        letter-spacing: 0px;
        font-size: 17px;
        font-weight: $font-weight-semibold;
        color: #454545;
        text-transform: uppercase;
        text-align: center;
        background-color: $color-font-light;
        border-radius: $rounded;
        padding: 8px 10px;
        margin-bottom: 1rem;

        .no-datetime-selected {
            display: inline-block;
            color: #454545;
        }
    }

    // hide regular input
    .input-display {
        display: none;
    }

    .error-message-wrap {
        margin-top: 0.5rem;
    }
}
</style>
